/**
 * REUSABLE Audit History List
 *
 * Drop into:
 *   <AuditHistoryList />                       // sales/admin global history
 *   <AuditHistoryList propertyId={uuid} />    // future property page
 *
 * Reads metrics JSON inline so no extra fetches per row. Memoized rows.
 *
 * Super-admin row actions: Download (when ready) + Delete. Delete hits
 * the server's hard-delete route which removes the audit row, every
 * encrypted source file, the encrypted output workbook, and the per-
 * audit /data directory. After a successful delete we drop the row
 * from local state so the list reflects the change without a full
 * refetch.
 */
import React, { useEffect, useState, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../../api';
import { useAuth } from '../../../context/AuthContext';
import { StatusBadge } from './AuditMetricsBadge';

const Row = memo(function Row({ a, isSuperAdmin, onDelete }) {
  const m = a.metrics || {};
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    const propLabel = a.property_name || a.property_label || 'this audit';
    const ok = window.confirm(
      `Permanently delete the audit for "${propLabel}"?\n\n` +
      `• Removes the workbook and every uploaded source file from disk.\n` +
      `• Cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await api.deleteAudit(a.id);
      onDelete(a.id);
    } catch (e) {
      alert(e.message);
      setDeleting(false);
    }
  };

  return (
    <tr style={deleting ? { opacity: 0.5 } : undefined}>
      <td>{a.created_at?.slice(0, 10)}</td>
      <td>
        <Link to={`/sales/audits/${a.id}`}>
          {a.property_name || <em>Unknown property</em>}
        </Link>
        {!a.property_id && <small className="audit-other-tag">Other</small>}
      </td>
      <td>{a.created_by_name || '—'}</td>
      <td>{m.total_units ?? '—'}</td>
      <td className={m.tow_risk_count > 0 ? 'audit-tow-risk' : ''}>
        {m.tow_risk_count ?? '—'}
      </td>
      <td>{m.compliance_rate != null ? `${m.compliance_rate}%` : '—'}</td>
      <td><StatusBadge status={a.status} /></td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {a.status === 'ready' && (
          <button
            type="button"
            className="audit-link-btn"
            onClick={() => api.downloadAuditWorkbook(a).catch((err) => alert(err.message))}
          >
            Download
          </button>
        )}
        {isSuperAdmin && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            title="Permanently delete this audit (super_admin only)"
            style={{
              marginLeft: a.status === 'ready' ? 12 : 0,
              padding: 0, background: 'none', border: 'none',
              color: '#b91c1c', fontSize: 13, fontWeight: 600,
              cursor: deleting ? 'wait' : 'pointer',
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        )}
      </td>
    </tr>
  );
});

export default function AuditHistoryList({ propertyId, limit = 50 }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.permission_tier === 'super_admin';

  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Debounce search input — fires server query at most every 300ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = propertyId
      ? api.getAuditsByProperty(propertyId)
      : api.listAudits({ limit, search: debouncedSearch, status: statusFilter });
    load
      .then(r => { if (!cancelled) setAudits(r.audits || []); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [propertyId, limit, debouncedSearch, statusFilter]);

  // Drop a deleted audit from local state. The server already wiped the
  // row + every disk artifact in a single transaction, so re-fetching
  // would just be a confirmation round-trip.
  const handleDeleted = (id) => {
    setAudits((prev) => prev.filter(a => a.id !== id));
  };

  // Memoize the rendered rows so re-renders of parent don't force row diffs
  const rows = useMemo(
    () => audits.map(a => (
      <Row key={a.id} a={a} isSuperAdmin={isSuperAdmin} onDelete={handleDeleted} />
    )),
    [audits, isSuperAdmin],
  );

  return (
    <div className="audit-history-list">
      {!propertyId && (
        <div className="audit-history-filters">
          <input
            type="text"
            placeholder="Search property or auditor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="generating">Generating</option>
            <option value="ready">Ready</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      )}

      {error && <div className="audit-error">{error}</div>}
      {loading ? (
        <div className="audit-loading">Loading audits…</div>
      ) : audits.length === 0 ? (
        <div className="audit-empty">
          {propertyId ? 'No audits for this property yet.' : 'No audits yet.'}
        </div>
      ) : (
        <div className="audit-table-wrap">
          <table className="audit-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Property</th>
                <th>Auditor</th>
                <th>Units</th>
                <th>TOW RISK</th>
                <th>Compliance</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
